import type {
  Map as MlMap,
  LayerSpecification,
  GeoJSONSource,
} from 'maplibre-gl';
import type { StructureCollection } from '../api/models';
import { buildTotemCollections, canonicalMaterials } from './totem-geometry';
import { loadMaterialImages, totemTextureImage } from './totem-materials';

export const TOTEM_FIELDS_SOURCE = 'totem-fields';
export const TOTEMS_SOURCE = 'totems';
export const TOTEM_DECALS_SOURCE = 'totem-decals';

// Screen px per ground meter at zoom 18 ≈ 8.8 (tuned against a ~20 m
// building reading as ~176 px); icons are 64 px tiles.
// LOCKSTEP with mobile's groundDecalSizeByZoom in map_view.dart.
const DECAL_SIZE_FACTOR = 8.8 / 64;

// totemLayerIds lists every clickable totem layer, for queryRenderedFeatures.
export function totemLayerIds(materials: string[] = canonicalMaterials): string[] {
  return [
    ...materials.map((p) => `totem-fields-${p}`),
    'totem-fields-color',
    'totem-decals',
    ...materials.map((p) => `totems-${p}`),
    'totems-color',
  ];
}

// The flat footprint-field fill layer for one material (footprintField is a
// material choice, so fields route per pattern like the extrusions do).
// Split out so the editor can add layers for fresh uploads mid-session.
export function fieldDefFor(material: string): LayerSpecification {
  return {
    id: `totem-fields-${material}`,
    type: 'fill',
    source: TOTEM_FIELDS_SOURCE,
    filter: ['==', ['get', 'pattern'], material],
    paint: {
      'fill-pattern': totemTextureImage(material),
      'fill-opacity': 0.95,
    },
  } as LayerSpecification;
}

// The fill-extrusion layer for one material — split out so the editor can add
// layers for freshly-uploaded materials mid-session.
export function extrusionDefFor(material: string): LayerSpecification {
  return {
    id: `totems-${material}`,
    type: 'fill-extrusion',
    source: TOTEMS_SOURCE,
    filter: ['==', ['get', 'pattern'], material],
    paint: {
      'fill-extrusion-pattern': totemTextureImage(material),
      'fill-extrusion-base': ['get', 'base'],
      'fill-extrusion-height': ['get', 'top'],
      'fill-extrusion-opacity': 0.97,
      'fill-extrusion-vertical-gradient': true,
    },
  } as LayerSpecification;
}

// totemLayerDefs returns the totem layer stack for a material list: farm
// fields as a flat pattern fill, the ground-decal symbol layer (decals sit
// under the prisms), one textured fill-extrusion layer per material, and a
// plain-color extrusion for pattern-less prisms. Pure so the set can be
// unit-tested; mirrors the mobile stack (addTotemExtrusionLayers + the decal
// symbol layer in plots_layer/world_totems).
export function totemLayerDefs(
  materials: string[] = canonicalMaterials,
): LayerSpecification[] {
  const defs: LayerSpecification[] = [
    ...materials.map(fieldDefFor),
    // Plain-color footprint fields (pattern '') — mirrors totems-color.
    {
      id: 'totem-fields-color',
      type: 'fill',
      source: TOTEM_FIELDS_SOURCE,
      filter: ['==', ['get', 'pattern'], ''],
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.95,
      },
    } as LayerSpecification,
    {
      id: 'totem-decals',
      type: 'symbol',
      source: TOTEM_DECALS_SOURCE,
      layout: {
        'icon-image': ['get', 'image'],
        // Ground-flat: the decal lies on the map plane and rotates/tilts
        // with the camera, like paint on the ground.
        'icon-pitch-alignment': 'map',
        'icon-rotation-alignment': 'map',
        'icon-rotate': ['get', 'rot'],
        // Constant ground size: feature `size` is meters; interpolate holds
        // it across zoom (exponential base 2, anchored at z18).
        'icon-size': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          14,
          ['*', ['get', 'size'], DECAL_SIZE_FACTOR / 16],
          20,
          ['*', ['get', 'size'], DECAL_SIZE_FACTOR * 4],
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    } as LayerSpecification,
  ];
  for (const material of materials) {
    defs.push(extrusionDefFor(material));
  }
  defs.push({
    id: 'totems-color',
    type: 'fill-extrusion',
    source: TOTEMS_SOURCE,
    filter: ['==', ['get', 'pattern'], ''],
    paint: {
      'fill-extrusion-color': ['get', 'color'],
      'fill-extrusion-base': ['get', 'base'],
      'fill-extrusion-height': ['get', 'top'],
      'fill-extrusion-opacity': 0.97,
      'fill-extrusion-vertical-gradient': true,
    },
  } as LayerSpecification);
  return defs;
}

// addTotemsLayer derives totem prisms + farm fields + ground decals from the
// public structure collection and adds (or refreshes) their sources and
// layers. Every feature carries the host structure's display properties, so
// clicks resolve details without a second lookup. Async because material
// PNGs decode on first call; refreshes resolve immediately.
export async function addTotemsLayer(
  map: MlMap,
  data: StructureCollection,
): Promise<void> {
  const collections = buildTotemCollections(
    data.features.map((f) => ({
      type: f.properties.structure_type,
      geometry: f.geometry,
      meta: {
        structure_type: f.properties.structure_type,
        name: f.properties.name,
        level: f.properties.level,
        builder_username: f.properties.builder_username ?? '',
      },
    })),
  );

  const existing = map.getSource(TOTEMS_SOURCE) as GeoJSONSource | undefined;
  if (existing) {
    existing.setData(collections.totems);
    (map.getSource(TOTEM_FIELDS_SOURCE) as GeoJSONSource).setData(collections.fields);
    (map.getSource(TOTEM_DECALS_SOURCE) as GeoJSONSource).setData(collections.decals);
    return;
  }

  for (const [name, image] of await loadMaterialImages(canonicalMaterials)) {
    if (!map.hasImage(totemTextureImage(name))) {
      map.addImage(totemTextureImage(name), image);
    }
  }
  map.addSource(TOTEM_FIELDS_SOURCE, { type: 'geojson', data: collections.fields });
  map.addSource(TOTEMS_SOURCE, { type: 'geojson', data: collections.totems });
  map.addSource(TOTEM_DECALS_SOURCE, { type: 'geojson', data: collections.decals });
  for (const layer of totemLayerDefs()) {
    map.addLayer(layer);
  }
}
